import { DatePipe } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { AppRoutingModule } from '@app/app-routing.module';
import { AppComponent } from '@app/app.component';
import { CoreModule } from '@core/core.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader, TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AuthService } from '@services/auth/auth.service';
import { ClaimService } from '@services/claims/claims.service';
import { ConfigService } from '@services/config/config.service';
import { SharedModule } from '@shared/shared.module';
import { LoggerService } from '@services/logger/logger.service';
import { get } from 'lodash';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { CastMember } from './core/classes/cast-member';
import { UserAccessService } from './core/services/user-access/user-access.service';
import { StoreModule } from '@ngrx/store';
import { RouterModule } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { ROOT_REDUCERS, metaReducers } from '@shared/store/reducers';
import { DataService } from './core/services/api';
import { keys, LocalStorageService } from './core/services/storage/local-storage.service';
import { firstValueFrom } from 'rxjs';

// AOT compilation support - TranslateHttpLoader v17 uses DI, no factory needed

@NgModule({
  declarations: [
    AppComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    SharedModule.forRoot(),
    CoreModule,
    AppRoutingModule,
    NgbModule,
    HttpClientModule,
    PopoverModule.forRoot(),
    TypeaheadModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader
      }
    }),
    StoreModule.forRoot(ROOT_REDUCERS, { metaReducers }),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  /* istanbul ignore next */
  providers: [
    AuthGuard,
    DatePipe,
    // TranslateHttpLoader v17 reads prefix/suffix from TRANSLATE_HTTP_LOADER_CONFIG via DI.
    // Without this provider it throws NG0201 at construction time.
    ...provideTranslateHttpLoader({ prefix: '/assets/i18n/', suffix: '.json' }),
    {
      provide: APP_INITIALIZER,
      useFactory: (config: ConfigService) => () => {
        console.log('calling config service from app module');

        return config.load()
          .then((result: any) => {

            return result;
          })
          .catch((err) => {
            return err;
          });
      },
      deps: [ConfigService],
      multi: true
    },
  ],
})
/* istanbul ignore next */
export class AppModule {
  public config: any;

  /* istanbul ignore next */
  constructor(
    private _authService: AuthService,
    private _claimService: ClaimService,
    private _loggerService: LoggerService,
    private _translateService: TranslateService,
    private _userAccessService: UserAccessService,
    private _router: Router,
    private _dataService: DataService,
    private _localStorage: LocalStorageService
  ) {
    this._translateService.setDefaultLang('en');

    // Resolve identity/claims eagerly so guards and cached lookups have data,
    // but never gate the root component render on the response — the UI must
    // mount immediately so users see auth/error screens instead of a blank page.
    this._claimService.getClaims().subscribe(
      (response) => {
        this._authService.castmember = new CastMember(response);
        this._authService.claims = response;
        this._userAccessService.setUser(this._authService.castmember);

        this.cacheData();

        const roles = get(response, 'roles', '') || [];
        this._loggerService.log(`User ${response.userProfile.userId} accessing roles`, roles);

        // Route-level authorization is enforced by AbilityGuard against the
        // decoded JWT's functionalAbilities. Don't pre-emptively redirect to
        // /access-denied here based solely on a missing/empty `roles` field —
        // some upstream responses populate abilities without an explicit role
        // list, and that legitimate users would otherwise get bounced.
      },
      (err) => {
        const unauthorizedError = 401;
        const forbiddenError = 403;

        /* istanbul ignore if */
        if (err && err.status === unauthorizedError) {
          this._loggerService.error(`Claims unauthorized error, attempting another log in`, err);
          this._authService.login();
        }

        /* istanbul ignore else */
        if (err && err.status === forbiddenError) {
          this._loggerService.error(`Claims forbidden error`, err);
          this._router.navigateByUrl(`/access-error/${forbiddenError}`);
        }
      }
    );
  }

  public cacheData() {
    const promises = [
      firstValueFrom(this._dataService.getSiteLocations()),
      firstValueFrom(this._dataService.getRegions()),
      firstValueFrom(this._dataService.getStatus()),
      firstValueFrom(this._dataService.getRoles())
    ];
    Promise.all(promises).then(results => {
      this._localStorage.saveData(keys.sites, results[0]);
      this._localStorage.saveData(keys.regions, results[1]);
      this._localStorage.saveData(keys.characterStatus, results[2]);
      this._localStorage.saveData(keys.roles, results[3]);
    });
  }
}
